import React, { useState } from 'react';
import { supabase } from '../supabase';
import { APP_VERSION_LABEL, APP_TAGLINE } from './Header';
import wordmark from '../assets/jvista-wordmark.png';

const inputStyle = {
  width: '100%', padding: '10px 12px',
  background: '#131920', border: '1px solid #2a3f55', color: '#e5ecf3',
  fontFamily: 'IBM Plex Mono, monospace', fontSize: 14,
  borderRadius: 3, outline: 'none', boxSizing: 'border-box',
};
const labelStyle = {
  display: 'block', marginBottom: 4, fontSize: 11,
  color: '#8ba3bb', fontFamily: 'IBM Plex Mono, monospace',
  textTransform: 'uppercase', letterSpacing: 0.5,
};
const btnStyle = {
  width: '100%', padding: '12px',
  background: 'rgba(0,212,255,0.1)', border: '1px solid #00d4ff',
  color: '#00d4ff', borderRadius: 3, cursor: 'pointer',
  fontFamily: 'IBM Plex Mono, monospace', fontSize: 14,
};
const btnDisabled = { ...btnStyle, opacity: 0.4, cursor: 'not-allowed' };

const MIN_LEN = 8;

// Shown when the user arrives via a Supabase invite or password-reset link.
// Supabase auto-exchanges the link token for a session (so onAuthStateChange
// reports SIGNED_IN / PASSWORD_RECOVERY), but the account has no password the
// user chose — so the app MUST force one here instead of granting access.
// onDone() is called after the password is set; the caller drops recovery mode.
export default function SetPasswordPanel({ email, onDone }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);   // {kind, text}

  async function handleSubmit(e) {
    e.preventDefault();
    if (!supabase) return;
    if (password.length < MIN_LEN) {
      setStatus({ kind: 'err', text: `Password must be at least ${MIN_LEN} characters.` });
      return;
    }
    if (password !== confirm) {
      setStatus({ kind: 'err', text: 'Passwords do not match.' });
      return;
    }
    setLoading(true);
    setStatus(null);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (error) {
      setStatus({ kind: 'err', text: error.message || 'Could not set password.' });
      return;
    }
    setStatus({ kind: 'ok', text: 'Password set. Signing you in…' });
    // Clear the invite/recovery token from the URL so a refresh doesn't
    // re-enter recovery mode, then hand control back to the app.
    try { window.history.replaceState(null, '', window.location.pathname); } catch { /* noop */ }
    if (onDone) onDone();
  }

  const statusColors = {
    err: { border: '#f87171', color: '#f87171', bg: 'rgba(248,113,113,0.05)' },
    ok:  { border: '#4ade80', color: '#4ade80', bg: 'rgba(74,222,128,0.05)' },
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: '#080b0f',
      fontFamily: 'IBM Plex Mono, monospace',
    }}>
      <form onSubmit={handleSubmit} style={{
        width: 360, padding: 32,
        background: '#0b1016', border: '1px solid #1e2d3d', borderRadius: 6,
      }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img src={wordmark} alt={`JVISTA — ${APP_TAGLINE}`}
               style={{ width: 220, maxWidth: '100%', display: 'block',
                        margin: '0 auto', borderRadius: 6 }} />
          <div style={{ fontSize: 10, color: '#8ba3bb',
                        letterSpacing: 1, marginTop: 8 }}>
            {APP_VERSION_LABEL} · {APP_TAGLINE}
          </div>
        </div>

        <div style={{
          marginBottom: 16, fontSize: 12, color: '#c9d6e3', lineHeight: 1.5,
        }}>
          Welcome to JVISTA{email ? `, ${email}` : ''}. Create a password to
          finish setting up your account.
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle} htmlFor="sp-pw">New password</label>
          <input id="sp-pw" type="password" required autoFocus
                 minLength={MIN_LEN} style={inputStyle}
                 value={password} onChange={e => setPassword(e.target.value)} />
        </div>
        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle} htmlFor="sp-pw2">Confirm password</label>
          <input id="sp-pw2" type="password" required
                 minLength={MIN_LEN} style={inputStyle}
                 value={confirm} onChange={e => setConfirm(e.target.value)} />
        </div>

        {status && (
          <div style={{
            padding: 10, marginBottom: 14,
            border: `1px solid ${statusColors[status.kind].border}`,
            color: statusColors[status.kind].color,
            background: statusColors[status.kind].bg,
            fontSize: 12, borderRadius: 3, lineHeight: 1.5,
          }}>
            {status.text}
          </div>
        )}

        <button type="submit" style={loading ? btnDisabled : btnStyle}
                disabled={loading}>
          {loading ? 'Setting password…' : 'Set Password & Continue'}
        </button>
      </form>
    </div>
  );
}
